import { useState, useEffect } from 'react'
import { ArrowRight, ArrowUpRight, Menu, X } from 'lucide-react'

const STORE_URL = import.meta.env.VITE_CHROME_WEB_STORE_URL as string | undefined

const DEMO_CLIPS = [
  { text: 'sk_test_51H8xQ2eZvKYlo2C…', host: 'dashboard.stripe.com', time: '1h ago' },
  { text: 'docker compose -f docker-compose.dev.yml up', host: 'docs.docker.com', time: '3h ago' },
  { text: 'ALTER TABLE notes ADD COLUMN custom_title…', host: 'supabase.com', time: 'yesterday' },
]

function JotMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="32" cy="30" r="10" />
      <rect x="54" y="16" width="16" height="68" rx="5" />
    </svg>
  )
}

export function ExtensionHome() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem('theme')
    if (stored === 'dark') return true
    if (stored === 'light') return false
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    document.title = 'JOT Clipboard — search everything you copy'
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.documentElement.classList.toggle('lp-dark', isDark)
    document.documentElement.classList.toggle('dark', isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
    return () => {
      document.documentElement.classList.remove('lp-dark')
    }
  }, [isDark])

  const chromeCta = STORE_URL ? (
    <a href={STORE_URL} className="lp-cta-primary" target="_blank" rel="noopener noreferrer">
      Add to Chrome
      <ArrowRight className="w-4 h-4" />
    </a>
  ) : (
    <a href="#how" className="lp-cta-primary">
      Get the extension
      <ArrowRight className="w-4 h-4" />
    </a>
  )

  return (
    <div className={`lp lp--extension${isDark ? ' lp--dark' : ''}`}>
      <nav className={`lp-nav${scrolled ? ' lp-nav--scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <a href="/home" className="lp-wordmark">JOT</a>

          <div className="lp-nav-center">
            <a href="/home" className="lp-nav-link">Clipboard</a>
            <a href="/editor" className="lp-nav-link">Editor</a>
            <a href="/blogs" className="lp-nav-link">Journey</a>
          </div>

          <div className="lp-nav-right">
            <button className="lp-theme-toggle" onClick={() => setIsDark(d => !d)} aria-label="Toggle theme">
              <span className="lp-theme-track">
                <span className={`lp-theme-thumb${isDark ? ' lp-theme-thumb--on' : ''}`} />
              </span>
              <span className="lp-theme-label">{isDark ? 'Light' : 'Dark'}</span>
            </button>
            <a href="/" className="lp-open-btn">Open editor <ArrowUpRight className="w-3.5 h-3.5" /></a>
            <button className="lp-hamburger" onClick={() => setMobileMenuOpen(o => !o)}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lp-mobile-nav">
            <a href="/home" onClick={() => setMobileMenuOpen(false)}>Clipboard</a>
            <a href="/editor" onClick={() => setMobileMenuOpen(false)}>Editor</a>
            <a href="/blogs" onClick={() => setMobileMenuOpen(false)}>Journey</a>
            <a href="/" className="lp-mobile-cta">Open editor →</a>
          </div>
        )}
      </nav>

      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-left">
            <p className="lp-hero-eyebrow">The Chrome extension</p>
            <h1 className="lp-hero-headline">
              Everything you copy.<br />Searchable from <em>any page</em>.
            </h1>
            <p className="lp-hero-body">
              JOT Clipboard remembers what you copy on the web — and the page it
              came from. Press ⌘⇧K to find it again. Clips stay on this device.
              Search the last 7 days free; full history is $30 once.
            </p>
            <div className="lp-hero-actions">
              {chromeCta}
              <a href="/subscribe" className="lp-nav-link">Unlock full history</a>
              <span className="lp-hero-proof">Local clips &nbsp;·&nbsp; 7 days free &nbsp;·&nbsp; $30 lifetime</span>
            </div>
          </div>

          <div className="lp-hero-right" aria-hidden>
            <div className="xh-stage">
              <div className="xh-browser">
                <div className="xh-browser-bar">
                  <span className="xh-dot" />
                  <span className="xh-dot" />
                  <span className="xh-dot" />
                  <span className="xh-url">docs.stripe.com/api/charges</span>
                </div>
                <div className="xh-page">
                  <div className="xh-line w70" />
                  <div className="xh-line w95" />
                  <div className="xh-line w55" />
                </div>
              </div>
              <div className="xh-spotlight">
                <div className="xh-search">
                  <JotMark className="xh-mark" />
                  <span className="xh-placeholder">Search everything you've copied…</span>
                  <kbd>esc</kbd>
                </div>
                <div className="xh-dropdown">
                  {DEMO_CLIPS.map(clip => (
                    <div key={clip.host + clip.time} className="xh-row">
                      <p className="xh-row-text">{clip.text}</p>
                      <p className="xh-row-meta">
                        <span className="xh-source-dot" />
                        {clip.host}
                        <span className="xh-sep">·</span>
                        {clip.time}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="lp-section">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <span className="lp-label">How it works</span>
            <h2 className="lp-section-title">Copy once.<br />Find it later.</h2>
          </div>
          <div className="xh-steps">
            <article className="xh-step">
              <span className="xh-step-num">01</span>
              <h3>It captures as you copy</h3>
              <p>Cmd+C, Cut, or a site’s Copy button. The text and the page it came from are saved automatically.</p>
            </article>
            <article className="xh-step">
              <span className="xh-step-num">02</span>
              <h3>Search from any page</h3>
              <p>⌘⇧K drops a search field on the page you’re on. Type, arrow, Enter to copy it back.</p>
            </article>
            <article className="xh-step">
              <span className="xh-step-num">03</span>
              <h3>Stays on this device</h3>
              <p>Clips live in Chrome’s local storage. Nobody else can read them. Paying only unlocks history older than a week.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="lp-section lp-section--alt">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <span className="lp-label">Why it exists</span>
            <h2 className="lp-section-title">A clipboard that remembers the source.</h2>
            <p className="lp-section-sub">
              The notes editor is still here — a blank page when you want to write.
              The extension is for everything you already copied and can’t find.
            </p>
          </div>
          <div className="xh-split">
            <a href="/home" className="xh-product is-current">
              <JotMark className="xh-product-mark" />
              <h3>Clipboard</h3>
              <p>Chrome extension. Captures copies. Spotlight search.</p>
              <span>You are here</span>
            </a>
            <a href="/editor" className="xh-product">
              <span className="xh-product-word">JOT</span>
              <h3>Editor</h3>
              <p>Local-first notes. Instant. Sync if you want it.</p>
              <span>Open the landing →</span>
            </a>
          </div>
        </div>
      </section>

      <section className="lp-final">
        <div className="lp-final-inner">
          <JotMark className="xh-final-mark" />
          <p className="lp-final-sub">Copy something. Press ⌘⇧K. That’s the product.</p>
          {chromeCta}
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <span className="lp-footer-wordmark">JOT</span>
          <span className="lp-footer-copy">© {new Date().getFullYear()} JOT. All rights reserved.</span>
          <div className="lp-footer-links">
            <a href="/home" className="lp-footer-link">Clipboard</a>
            <a href="/editor" className="lp-footer-link">Editor</a>
            <a href="/" className="lp-footer-link">App</a>
            <a href="/blogs" className="lp-footer-link">Journey</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
